import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

// Generic components and actions
import { DataTable, DeleteModal, Excel } from "CommonComponent";
import {
  fetchConfigurations,
  deleteConfiguration,
  exportConfigurations,
} from "pages/AdminManage/actions/ConfigurationActions";
import AccessRights from "utils/AccessRights";

// Component-specific imports
import { Header } from "./component";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import SourceAddUpdateModal from "./component/SourceAddUpdateModal";

// --- Component Configuration ---
const CONFIG_TYPE = "source";
const CAPITALIZED_TYPE = "Source";

const Source = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // Select from the generic 'configurationState' slice
  const { lists, redirectToList } = useSelector((s) => s.configurationState);
  const session = useSelector((s) => s.session);

  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Load initial data
  useEffect(() => {
    if (session.current_page_permissions?.page_url === location.pathname) {
      dispatch(fetchConfigurations(CONFIG_TYPE, { object_viewed_id }));
    }
  }, [
    session.current_page_permissions,
    location.pathname,
    dispatch,
    object_viewed_id,
  ]);

  // Reload list after a CUD operation
  useEffect(() => {
    if (redirectToList) {
      dispatch(fetchConfigurations(CONFIG_TYPE, { object_viewed_id }));
    }
  }, [redirectToList, dispatch, object_viewed_id]);

  // ---------- CRUD HANDLERS ----------

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedRow(null);
    setShowAddUpdateModal(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setIsEdit(true);
    setShowAddUpdateModal(true);
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!selectedRow) return;
    dispatch(
      deleteConfiguration(CONFIG_TYPE, selectedRow.id, object_viewed_id),
    );
    setShowDeleteModal(false);
  };

  // ---------- ACTION ICONS ----------

  const getActions = (row) => (
    <div
      className="actionClass"
      style={{ textAlign: "center", whiteSpace: "nowrap" }}
    >
      {AccessRights(
        session.current_page_permissions,
        "edit",
        row.created_by,
      ) && (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`edit-${row.id}`}>Edit</Tooltip>}
          container={document.body}
        >
          {({ ref, ...triggerHandler }) => (
            <span
              ref={ref}
              {...triggerHandler}
              style={{ display: "inline-block", marginRight: "10px" }}
            >
              <img
                src="/assets/images/edit-icon.svg"
                alt="Edit"
                onClick={() => handleEdit(row)}
                style={{ cursor: "pointer" }}
              />
            </span>
          )}
        </OverlayTrigger>
      )}

      {AccessRights(
        session.current_page_permissions,
        "delete",
        row.created_by,
      ) && (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`delete-${row.id}`}>Delete</Tooltip>}
          container={document.body}
        >
          {({ ref, ...triggerHandler }) => (
            <span
              ref={ref}
              {...triggerHandler}
              style={{ display: "inline-block" }}
            >
              <img
                src="/assets/images/delete-icon.svg"
                alt="Delete"
                onClick={() => handleDelete(row)}
                style={{ cursor: "pointer" }}
              />
            </span>
          )}
        </OverlayTrigger>
      )}
    </div>
  );

  // ---------- TABLE COLUMNS ----------

  const columns = useMemo(
    () => [
      {
        id: "value",
        header: `${CAPITALIZED_TYPE} Value`,
        accessor: (row) => row.value,
        sortable: true,
      },
      {
        id: "sort_order",
        header: "Sort Order",
        accessor: (row) => row.sort_order,
        sortable: true,
      },

      {
        id: "actions",
        header: "Actions",
        accessor: null,
        cell: (_, row) => getActions(row),
        sortable: false,
      },
    ],
    [session.current_page_permissions],
  );

  // Get the specific list from the 'lists' object in the reducer
  const rows = lists[CONFIG_TYPE] || [];

  // ---------- EXPORT ----------

  const handleExport = () => {
    dispatch(exportConfigurations(CONFIG_TYPE, {}, object_viewed_id));
  };

  // ---------- RENDER ----------

  return (
    <div className="wrapper-div">
      <Header
        title={CAPITALIZED_TYPE}
        onAdd={handleAdd}
        object_viewed_id={object_viewed_id}
      />

      <div className="d-flex justify-content-end align-items-center pb-4 px-4 position-sticky top-70 bg-white z-1">
        <div className="d-flex align-items-center gap-3">
          <Excel handleExport={handleExport} />
          {/* You can add a generic Filter component here if needed */}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        serverSide={false}
        showSearch={false}
        className="table-container"
      />

      {showAddUpdateModal && (
        <SourceAddUpdateModal
          show={showAddUpdateModal}
          handleClose={() => setShowAddUpdateModal(false)}
          isEdit={isEdit}
          data={selectedRow}
          object_viewed_id={object_viewed_id}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={confirmDelete}
          message={`Are you sure you want to delete this ${CAPITALIZED_TYPE.toLowerCase()}?`}
        />
      )}
    </div>
  );
};

export default Source;
